import { AnimatePresence, motion } from "motion/react";
import { ReactNode, memo, useEffect, useRef, useState } from "react";
import { BsArrowLeft, BsXLg } from "react-icons/bs";

import { filterClasses } from "../../../Helpers/FilterClasses";
import useAutoFocus from "../../../Hooks/useAutoFocus";
import useKeyPressAction from "../../../Hooks/useKeyPressAction";

interface DetailsPanelProps {
  defaultTabIndex?: number;
  isOpen: boolean;
  onClose: () => void;
  renderTab: (tab: string, navigateForward: (tab?: string) => void, isPanelOpen: boolean) => ReactNode;
  tabs: string[];
}

const BaseDetailsPanel = ({ defaultTabIndex = 0, onClose, isOpen, renderTab, tabs }: DetailsPanelProps) => {
  const [currentTabIndex, setCurrentTabIndex] = useState(defaultTabIndex);
  const [direction, setDirection] = useState(1);
  const panelRef = useRef<HTMLDivElement>(null);
  useAutoFocus(panelRef, [isOpen, currentTabIndex]);
  useKeyPressAction({
    callback: onClose,
    key: "Escape",
    targetEl: panelRef.current,
  });

  useEffect(() => {
    if (!isOpen) {
      setDirection(1);
      setCurrentTabIndex(defaultTabIndex);
    }
  }, [onClose])

  const handlePrevTab = () => {
    if (currentTabIndex > 0) {
      setDirection(-1);
      setCurrentTabIndex((prev) => prev - 1);
    } else {
      onClose();
    }
  };

  const handleNextTab = (tab?: string) => {
    if (tab) {
      const index = tabs.indexOf(tab);
      if (index > -1) {
        setDirection(1);
        setCurrentTabIndex(index);
        return;
      }
    }

    if (currentTabIndex < tabs.length - 1) {
      setDirection(1);
      setCurrentTabIndex((prev) => prev + 1);
    }
  };

  return (
    <div
      aria-expanded={isOpen}
      className={filterClasses(
        "bg-white h-[calc(100%-70px)] overflow-y-auto z-50 top-[70px] flex-col flex fixed duration-300 transition-all shadow-[-4px_2px_8px_rgba(0,0,0,0.1)] right-0 px-4 md:px-8 py-4 w-1/3 [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar]:rounded-md [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-grey-400 [&::-webkit-scrollbar-thumb]:rounded-md",
        isOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0",
      )}
      ref={panelRef}
      tabIndex={-1}
    >
      <div className="w-full py-1.5 h-16 flex space-x-1 md:space-x-2 items-center justify-between mb-8">
        {/* Back Button */}
        <div className="w-1/4 flex justify-start">
          <button
            className="flex hover:text-gray-400 transition-colors duration-200 items-center space-x-1.5 md:space-x-2.5 p-1.5"
            onClick={handlePrevTab}
          >
            <BsArrowLeft className="size-4 md:size-6" strokeWidth={0.4} />
            <span className="text-lg font-semibold">Back</span>
          </button>
        </div>

        {/* Heading */}
        <div className="px-1.5 w-1/2 flex items-center justify-center overflow-x-hidden">
          <h5 className="font-semibold truncate tracking-wide md:text-xl">{tabs[currentTabIndex]}</h5>
        </div>

        {/* Close Button */}
        <div className="w-1/4 flex justify-end">
          <button aria-label="Close Panel" className="size-9 p-2.5 flex items-center justify-center rounded-full" onClick={onClose}>
            <BsXLg className="hover:stroke-gray-400 transition-colors duration-200" strokeWidth={2} size={20} />
          </button>
        </div>
      </div>

      {/* Tab */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          exit={{ x: -direction * 100, opacity: 0 }}
          initial={{ x: direction * 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          key={tabs[currentTabIndex]}
        >
          {renderTab(tabs[currentTabIndex], handleNextTab, isOpen)}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export const DetailsPanel = memo(BaseDetailsPanel);
